import { ChangeDetectionStrategy, Component, inject, output } from '@angular/core';
import { LucideAngularModule, Mic, MicOff, Video, VideoOff, UserPlus, Share, SwitchCamera, Phone } from 'lucide-angular';
import { SalleAppel } from '../../../core/services/salle-appel.service';
import { TranslationService } from '../../../core/services/translation.service';

/**
 * Barre de controle et bouton Raccrocher — maquette « Appels — palette claire », ecrans 2, 5 et 6.
 *
 * Une pastille de boutons ronds de 46 px (micro, camera, ajouter, partager l'ecran, changer de camera),
 * puis, SEPARE en dessous, le grand bouton Raccrocher de 68 px. Les boutons de bascule agissent sur la
 * salle ; Ajouter et Raccrocher sont signales a l'ecran parent, qui sait quoi en faire.
 *
 * Deux themes : clair par defaut (la maquette), sombre sous `html.dark` (les anciens ecrans d'appel).
 * Etat actif = indigo clair (camera allumee, partage en cours). Micro coupe = rose clair, pour qu'on
 * voie d'un coup d'oeil qu'on ne vous entend pas.
 */
@Component({
  selector: 'app-barre-controles-appel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LucideAngularModule],
  template: `
    <div class="flex-none px-5 pt-2.5">
      <div class="flex items-center justify-center gap-3 rounded-full px-3.5 py-[11px] border bg-white border-surface-200 shadow-[0_8px_24px_rgba(15,23,42,0.1)]
                  dark:bg-white/[0.07] dark:border-white/10 dark:backdrop-blur-[18px] dark:shadow-[0_12px_30px_-12px_rgba(0,0,0,0.5)]">
        <button type="button" class="bouton" (click)="salle.basculerMicro()" [attr.aria-pressed]="!salle.micro()"
                [attr.aria-label]="salle.micro() ? t().calls.mic_mute : t().calls.mic_unmute"
                [class]="salle.micro() ? neutre : coupe">
          <lucide-icon [img]="salle.micro() ? Mic : MicOff" [size]="20" aria-hidden="true"></lucide-icon>
        </button>

        <button type="button" class="bouton" (click)="salle.basculerCamera()" [attr.aria-pressed]="salle.camera()"
                [attr.aria-label]="salle.camera() ? t().calls.camera_stop : t().calls.camera_start"
                [class]="salle.camera() ? actif : neutre">
          <lucide-icon [img]="salle.camera() ? Video : VideoOff" [size]="20" aria-hidden="true"></lucide-icon>
        </button>

        <button type="button" class="bouton" (click)="ajouter.emit()" [attr.aria-label]="t().calls.add_participant" [class]="neutre">
          <lucide-icon [img]="UserPlus" [size]="20" aria-hidden="true"></lucide-icon>
        </button>

        @if (salle.partagePossible) {
          <button type="button" class="bouton" (click)="salle.basculerPartage()" [attr.aria-pressed]="salle.partage()"
                  [attr.aria-label]="salle.partage() ? t().calls.share_stop : t().calls.share_screen"
                  [class]="salle.partage() ? actif : neutre">
            <lucide-icon [img]="Share" [size]="20" aria-hidden="true"></lucide-icon>
          </button>
        }

        @if (salle.plusieursCameras()) {
          <button type="button" class="bouton" (click)="salle.changerCamera()" [attr.aria-label]="t().calls.switch_camera" [class]="neutre">
            <lucide-icon [img]="SwitchCamera" [size]="20" aria-hidden="true"></lucide-icon>
          </button>
        }
      </div>
    </div>

    <div class="flex-none flex items-center justify-center pt-4" style="padding-bottom:calc(env(safe-area-inset-bottom,0px) + 34px)">
      <button type="button" class="bouton border bg-danger-50 border-danger-200 text-danger-600
                dark:border-white/15 dark:text-white dark:bg-[linear-gradient(160deg,#fb7185,#e11d48_65%,#be123c)] dark:shadow-[0_18px_34px_-10px_rgba(225,29,72,0.7)]"
              (click)="raccrocher.emit()" [attr.aria-label]="t().calls.hang_up" style="width:68px;height:68px">
        <span class="inline-flex rotate-[135deg]" aria-hidden="true"><lucide-icon [img]="Phone" [size]="26"></lucide-icon></span>
      </button>
    </div>
  `,
  styles: [
    `
      .bouton { width:46px; height:46px; border-radius:9999px; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:transform .15s ease, background .15s ease; }
      .bouton:active { transform:scale(0.92); }
    `,
  ],
})
export class BarreControlesAppelComponent {
  readonly salle = inject(SalleAppel);
  readonly t = inject(TranslationService).t;

  readonly ajouter = output<void>();
  readonly raccrocher = output<void>();

  /** Classes des trois etats d'un bouton rond (clair, puis variante sombre). */
  readonly neutre = 'bouton border bg-white border-surface-200 text-surface-700 dark:bg-white/[0.09] dark:border-transparent dark:text-[#f4f4fe]';
  readonly actif = 'bouton border bg-primary-50 border-primary-200 text-primary-700 dark:border-transparent dark:text-white dark:bg-[linear-gradient(160deg,#a5b4fc,#4f46e5)] dark:shadow-[0_8px_18px_-6px_rgba(79,70,229,0.65)]';
  readonly coupe = 'bouton border bg-danger-50 border-danger-200 text-danger-600 dark:border-transparent dark:bg-[#f4f4fe] dark:text-[#1c1550]';

  readonly Mic = Mic;
  readonly MicOff = MicOff;
  readonly Video = Video;
  readonly VideoOff = VideoOff;
  readonly UserPlus = UserPlus;
  readonly Share = Share;
  readonly SwitchCamera = SwitchCamera;
  readonly Phone = Phone;
}
